import { v4 as uuidv4 } from 'uuid'
import { genPrompt } from '@/api/prompt'
import { genOssImage } from '@/utils/ossPath'
import { Message } from 'element-ui'
import { getLastUsedBaseUrl, cudaIndexNum } from '@/utils/comfyUiRequest'
import axios from 'axios'
import cuda from '@/utils/checkCuda'
import random from 'random'
import { getTaskStart, getTaskEnd } from '@/api/user'
import {
  CHANGE_FINGER_V1,
  CHANGE_FINGER_V2,
  T_BLEND_IMAGE,
  T_IMAGE_SCALING,
  T_QUALITY_OPT
} from '@/enums/common/modules'
import maoyanWorkflow from '@/api/json_api/FLUX.2猫眼美甲替换.json'
import kleinWorkflow from '@/api/json_api/F2.Klein甲型替换-精简.json'
import kleinPureWorkflow from '@/api/json_api/FLUX2.klien常规美甲替换.json'
import blendImageWorkflow from '@/api/json_api/Klein 9B图像光影融合.json'
import broadPictureWorkflow from '@/api/json_api/扩图Api.json'

import { OSS_ACCESS_KEY_ID, OSS_ACCESS_KEY_SECRET } from '@/utils/secretCrypto'
// 生成唯一输出文件名
function genFilename() {
  return `["tmp-comfyui/${String(Date.now()) + String(random.int(1, 99999))}.png"]`
}

// 通用工作流提交：genPrompt → WebSocket 进度 → /history 解析输出节点 → resolve(imageUrls[])
function submitWorkflow({ promptData, taskType, inputImages, outputNodeIds, progressCallback, onTaskStart }) {
  return new Promise((resolve, reject) => {
    const clientId = uuidv4()
    let taskLogId = null
    const data = { prompt: promptData, client_id: clientId }

    const fetchImageFilename = async (promptId) => {
      const historyRequest = axios.create({
        baseURL: getLastUsedBaseUrl(),
        timeout: 5000,
        headers: { 'Content-Type': 'application/json' }
      })
      const response = await historyRequest.get(`/history/${promptId}`)
      const promptInfo = response.data[promptId]
      const promptIndex = 2 // prompt信息的下标
      const imagePaths = []
      for (const nodeId of outputNodeIds) {
        const nodeInfo = promptInfo.prompt && promptInfo.prompt[promptIndex] && promptInfo.prompt[promptIndex][nodeId]
        if (nodeInfo && nodeInfo.inputs && nodeInfo.inputs.filename) {
          try {
            const tmp = JSON.parse(nodeInfo.inputs.filename)
            imagePaths.push(genOssImage(tmp[0]))
          } catch (e) {
            console.error(`OSS节点${nodeId} filename解析失败:`, e.message)
          }
        }
      }
      return imagePaths
    }

    const listenToProgress = (promptId) => {
      const ws = new WebSocket(`${getLastUsedBaseUrl()}/ws?clientId=${clientId}`)
      const wsTimeout = setTimeout(() => {
        Message.warning('网络连接超时,请重新尝试!')
        ws.close()
        reject(new Error('WebSocket连接超时'))
      }, 2000)

      ws.onopen = async () => {
        clearTimeout(wsTimeout)
        try {
          const rep = await getTaskStart({
            type: taskType,
            prompt: '',
            input_images: (inputImages || []).filter(Boolean)
          })
          taskLogId = rep.data.task.id
          if (typeof onTaskStart === 'function') {
            try { onTaskStart(taskLogId) } catch (e) { /* 不阻断主流程 */ }
          }
        } catch (err) {
          console.log(err)
        }
      }
      ws.onmessage = async (event) => {
        clearTimeout(wsTimeout)
        if (typeof event.data !== 'string') return
        try {
          const msg = JSON.parse(event.data)
          if (msg.type === 'executing') {
            if (msg.data.node === null && msg.data.prompt_id === promptId) {
              ws.close()
              const imagePaths = await fetchImageFilename(promptId)
              if (taskLogId) {
                const info = { details: imagePaths.map(path => ({ path })) }
                getTaskEnd({ result: JSON.stringify(info) }, taskLogId)
              }
              resolve(imagePaths)
            }
          } else if (msg.type === 'progress') {
            const current = (msg.data.value / msg.data.max * 100).toFixed(2)
            if (typeof progressCallback === 'function') {
              progressCallback(current)
            }
          }
        } catch (error) {
          console.error('WebSocket消息解析失败:', error)
        }
      }
      ws.onerror = (error) => {
        reject(error)
      }
      ws.onclose = async () => {
        try {
          await cuda.changeCudaCurrentStatus(cudaIndexNum(), 'leisure')
        } catch (error) { console.log(error) }
      }
    }

    ;(async () => {
      try {
        const result = await genPrompt(data)
        if (result && result.prompt_id) {
          listenToProgress(result.prompt_id)
        } else {
          reject(new Error('提交任务失败：未返回 prompt_id'))
        }
      } catch (error) {
        reject(error)
      }
    })()
  })
}

// 替换甲片：5 参变量替换（同 TestFingernailChange.replaceWorkflowVariables）
function replaceNailWorkflowVariables(workflow, seed, referenceImageUrl, inputImageUrl, filename) {
  const workflowCopy = JSON.parse(JSON.stringify(workflow))
  for (const nodeId in workflowCopy) {
    const node = workflowCopy[nodeId]
    if (!node.inputs) continue
    for (const inputKey in node.inputs) {
      const inputValue = node.inputs[inputKey]
      if (typeof inputValue !== 'string') continue
      if (inputValue.includes('{{SEED}}')) {
        node.inputs[inputKey] = seed
      } else if (inputValue.includes('{{REFERENCE_IMAGE_URL}}')) {
        node.inputs[inputKey] = referenceImageUrl
      } else if (inputValue.includes('{{INPUT_IMAGE_URL}}')) {
        node.inputs[inputKey] = inputImageUrl
      } else if (/\{\{OUTPUT_FILENAME(?:_\d+)?\}\}_preview/.test(inputValue)) {
        node.inputs[inputKey] = inputValue.replace(/\{\{OUTPUT_FILENAME(?:_(\d+))?\}\}_preview/g, (match, num) => {
          const baseName = filename.replace(/\[|\]|"/g, '').replace('.png', '')
          if (num) {
            return `["${baseName}_${num}_preview.png"]`
          }
          return `["${baseName}_preview.png"]`
        })
      } else if (/\{\{OUTPUT_FILENAME(?:_\d+)?\}\}/.test(inputValue)) {
        node.inputs[inputKey] = inputValue.replace(/\{\{OUTPUT_FILENAME(?:_(\d+))?\}\}/g, (match, num) => {
          if (num) {
            const baseName = filename.replace(/\[|\]|"/g, '').replace('.png', '')
            return `["${baseName}_${num}.png"]`
          }
          return filename
        })
      } else if (inputValue.includes('{{OSS_ACCESS_KEY_ID}}')) {
        node.inputs[inputKey] = OSS_ACCESS_KEY_ID
      } else if (inputValue.includes('{{OSS_ACCESS_KEY_SECRET}}')) {
        node.inputs[inputKey] = OSS_ACCESS_KEY_SECRET
      }
    }
  }
  return workflowCopy
}

// 光影溶图：4 参变量替换（同 BlendImage.replaceWorkflowVariables）
function replaceBlendWorkflowVariables(workflow, seed, filename, imageUrl) {
  const workflowCopy = JSON.parse(JSON.stringify(workflow))
  for (const nodeId in workflowCopy) {
    const node = workflowCopy[nodeId]
    if (!node.inputs) continue
    for (const inputKey in node.inputs) {
      const inputValue = node.inputs[inputKey]
      if (typeof inputValue !== 'string') continue
      if (inputValue.includes('{{SEED}}')) {
        node.inputs[inputKey] = seed
      } else if (inputValue.includes('{{OUTPUT_FILENAME}}')) {
        node.inputs[inputKey] = filename
      } else if (inputValue.includes('{{OSS_ACCESS_KEY_ID}}')) {
        node.inputs[inputKey] = OSS_ACCESS_KEY_ID
      } else if (inputValue.includes('{{OSS_ACCESS_KEY_SECRET}}')) {
        node.inputs[inputKey] = OSS_ACCESS_KEY_SECRET
      } else if (inputValue.includes('{{START_IMAGE_URL}}')) {
        node.inputs[inputKey] = imageUrl
      }
    }
  }
  return workflowCopy
}

// 无界扩图：对象参数变量替换（同 BroadPicture.replaceWorkflowVariables）
function replaceOutpaintWorkflowVariables(workflow, params) {
  const workflowCopy = JSON.parse(JSON.stringify(workflow))
  for (const nodeId in workflowCopy) {
    const node = workflowCopy[nodeId]
    if (!node.inputs) continue
    for (const inputKey in node.inputs) {
      const inputValue = node.inputs[inputKey]
      if (typeof inputValue !== 'string') continue
      if (inputValue.includes('{{IMAGE_URL}}')) {
        node.inputs[inputKey] = params.imageUrl
      } else if (inputValue.includes('{{OUTPUT_FILENAME}}')) {
        node.inputs[inputKey] = params.filename
      } else if (inputValue.includes('{{SEED}}')) {
        node.inputs[inputKey] = params.seed
      } else if (inputValue.includes('{{LEFT}}')) {
        node.inputs[inputKey] = params.left
      } else if (inputValue.includes('{{TOP}}')) {
        node.inputs[inputKey] = params.top
      } else if (inputValue.includes('{{RIGHT}}')) {
        node.inputs[inputKey] = params.right
      } else if (inputValue.includes('{{BOTTOM}}')) {
        node.inputs[inputKey] = params.bottom
      } else if (inputValue.includes('{{FEATHER}}')) {
        node.inputs[inputKey] = params.feather
      }
    }
  }
  return workflowCopy
}

// 替换甲片（非图库分支）
function runNailReplace(params, progressCallback, onTaskStart) {
  const { inputImageUrl, referenceImageUrl, nailShapeEnabled, selectedStyle } = params
  const seed = Math.floor(Math.random() * 100000000000000)
  const filename = genFilename()
  let workflow
  let outputNodeIds
  let taskType

  if (nailShapeEnabled) {
    // 甲型替换工作流
    workflow = JSON.parse(JSON.stringify(kleinWorkflow))
    if (selectedStyle === 'pure') {
      workflow['182'].inputs.strength_model = 0
      workflow['182'].inputs.strength_clip = 0
    } else if (selectedStyle === 'maoyan') {
      workflow['182'].inputs.strength_model = 0.8
      workflow['182'].inputs.strength_clip = 0.8
    } else if (selectedStyle === 'jelly') {
      workflow['182'].inputs.lora_name = 'F.2-Klein-果冻lora-9B-v1.safetensors'
      workflow['182'].inputs.strength_model = 0.8
      workflow['182'].inputs.strength_clip = 0.8
      workflow['65'].inputs.prompt = 'GGGDDD, TTTHHH, replace the nail tips onto the fingers for a seamless integration, maintaining the original shape and color of the nail tips, blending lighting and texture, and keeping the hand features unaltered.'
    }
    outputNodeIds = [196, 192, 193, 194]
    taskType = CHANGE_FINGER_V2
  } else if (selectedStyle === 'pure') {
    // 常规美甲替换工作流
    workflow = JSON.parse(JSON.stringify(kleinPureWorkflow))
    outputNodeIds = [164, 161, 162, 163]
    taskType = CHANGE_FINGER_V1
  } else {
    // 猫眼 / 果冻工作流
    workflow = JSON.parse(JSON.stringify(maoyanWorkflow))
    if (selectedStyle === 'jelly') {
      workflow['119'].inputs.lora_name = 'FLUX2_果冻lora_v.1.safetensors'
      workflow['65'].inputs.prompt = 'GGGDDD,(((半透明，能透过GGGDDD美甲看到手指轮廓)))，(((图1是"GGGDDD"美甲，保证图1"GGGDDD"表面为高光泽水润封层,呈现凝胶光泽水润质感，细节清晰)))，((将图1美甲换到图2的全部手指上，细节与颜色不变，图案完全保持一致))，(((指甲全部替换)))，(((保持美甲颜色不变)))，图2手部与其他内容像素、光影、细节保持不变,保证手部质感与纹理，完好的手'
    }
    outputNodeIds = [164, 161, 162, 163]
    taskType = CHANGE_FINGER_V1
  }

  const promptData = replaceNailWorkflowVariables(workflow, seed, referenceImageUrl, inputImageUrl, filename)
  return submitWorkflow({
    promptData,
    taskType,
    inputImages: [inputImageUrl, referenceImageUrl],
    outputNodeIds,
    progressCallback,
    onTaskStart
  })
}

// 光影溶图
function runLightBlend(params, progressCallback, onTaskStart) {
  const { imageUrl } = params
  const seed = Math.floor(Math.random() * 100000000000000)
  const filename = genFilename()
  const promptData = replaceBlendWorkflowVariables(blendImageWorkflow, seed, filename, imageUrl)
  return submitWorkflow({
    promptData,
    taskType: T_BLEND_IMAGE,
    inputImages: [imageUrl],
    outputNodeIds: [160],
    progressCallback,
    onTaskStart
  })
}

// 无界扩图
function runOutpaint(params, progressCallback, onTaskStart) {
  const { imageUrl, top, bottom, left, right, feather } = params
  const seed = Math.floor(Math.random() * 10000000000000000)
  const filename = genFilename()
  const promptData = replaceOutpaintWorkflowVariables(broadPictureWorkflow, {
    imageUrl,
    filename,
    seed,
    left,
    top,
    right,
    bottom,
    feather
  })
  return submitWorkflow({
    promptData,
    taskType: T_IMAGE_SCALING,
    inputImages: [imageUrl],
    outputNodeIds: [157],
    progressCallback,
    onTaskStart
  })
}

// 高清放大（内联 promptData，照搬 RealisticHD）
function runHdUpscale(params, progressCallback, onTaskStart) {
  const { imageUrl } = params
  const seed = Math.floor(Math.random() * 100000000000000)
  const filename = genFilename()
  const promptData = {
    15: {
      inputs: {
        upscale_by: ['26', 0],
        seed: seed,
        steps: 4,
        cfg: 1,
        sampler_name: 'euler',
        scheduler: 'beta',
        denoise: ['40', 0],
        mode_type: 'Linear',
        tile_width: 1024,
        tile_height: 1024,
        mask_blur: 30,
        tile_padding: 32,
        seam_fix_mode: 'None',
        seam_fix_denoise: 1,
        seam_fix_width: 64,
        seam_fix_mask_blur: 8,
        seam_fix_padding: 16,
        force_uniform_tiles: true,
        tiled_decode: false,
        image: ['230', 0],
        model: ['22', 0],
        positive: ['18', 0],
        negative: ['20', 0],
        vae: ['21', 0],
        upscale_model: ['17', 0]
      },
      class_type: 'UltimateSDUpscale'
    },
    17: {
      inputs: { model_name: '4x_NMKD-Siax_200k.pth' },
      class_type: 'UpscaleModelLoader'
    },
    18: {
      inputs: { text: ['19', 2], clip: ['44', 0] },
      class_type: 'CLIPTextEncode'
    },
    19: {
      inputs: {
        text_input: '',
        task: 'detailed_caption',
        fill_mask: true,
        keep_model_loaded: false,
        max_new_tokens: 1024,
        num_beams: 3,
        do_sample: true,
        output_mask_select: '',
        seed: seed,
        image: ['230', 0],
        florence2_model: ['51', 0]
      },
      class_type: 'Florence2Run'
    },
    20: {
      inputs: { text: '', clip: ['44', 0] },
      class_type: 'CLIPTextEncode'
    },
    21: {
      inputs: { vae_name: 'ae.safetensors' },
      class_type: 'VAELoader'
    },
    22: {
      inputs: { strength: 1, model: ['50', 0] },
      class_type: 'DifferentialDiffusion'
    },
    26: {
      inputs: { value: '3' },
      class_type: 'Float'
    },
    40: {
      inputs: { value: '0.23' },
      class_type: 'Float'
    },
    43: {
      inputs: {
        unet_name: 'FLUX1/flux1-dev-fp8.safetensors',
        weight_dtype: 'fp8_e4m3fn'
      },
      class_type: 'UNETLoader'
    },
    44: {
      inputs: {
        clip_name1: 't5xxl_fp8_e4m3fn.safetensors',
        clip_name2: 'clip_l.safetensors',
        type: 'flux',
        device: 'default'
      },
      class_type: 'DualCLIPLoader'
    },
    50: {
      inputs: {
        lora_name: '【人像大师】真实感亚洲人像写真_v1.0.safetensors',
        strength_model: 0.7,
        model: ['43', 0]
      },
      class_type: 'LoraLoaderModelOnly'
    },
    51: {
      inputs: {
        model: 'microsoft/Florence-2-base',
        precision: 'fp16',
        attention: 'sdpa',
        convert_to_safetensors: false
      },
      class_type: 'DownloadAndLoadFlorence2Model'
    },
    230: {
      inputs: { image_url: imageUrl },
      class_type: 'LoadImageFromURL'
    },
    231: {
      inputs: {
        filename: filename,
        access_key_id: OSS_ACCESS_KEY_ID,
        access_key_secret: OSS_ACCESS_KEY_SECRET,
        security_token: '',
        bucket_name: 'mj-comfy-ui',
        endpoint: 'oss-cn-shenzhen.aliyuncs.com',
        image: ['15', 0]
      },
      class_type: 'OSSUploadNode'
    }
  }
  return submitWorkflow({
    promptData,
    taskType: T_QUALITY_OPT,
    inputImages: [imageUrl],
    outputNodeIds: [231],
    progressCallback,
    onTaskStart
  })
}

// 统一分发器：按功能值调用对应实现，返回 Promise<imageUrls[]>
function runBatchTask(funcValue, params, progressCallback, onTaskStart) {
  switch (funcValue) {
    case 'nail_replace':
      return runNailReplace(params, progressCallback, onTaskStart)
    case 'light_blend':
      return runLightBlend(params, progressCallback, onTaskStart)
    case 'outpaint':
      return runOutpaint(params, progressCallback, onTaskStart)
    case 'hd_upscale':
      return runHdUpscale(params, progressCallback, onTaskStart)
    default:
      return Promise.reject(new Error(`不支持的批量功能: ${funcValue}`))
  }
}

export { runBatchTask }
